import { useEffect, useState } from "react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminPolicyPages() {
    const [pages, setPages] = useState([]);
    const [active, setActive] = useState(null);
    const [form, setForm] = useState({ title: "", bodyMarkdown: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/admin/policy-pages").then(({ data }) => {
            setPages(data || []);
            if ((data || []).length) {
                setActive(data[0].slug);
                setForm({ title: data[0].title, bodyMarkdown: data[0].bodyMarkdown });
            }
        });
    }, []);

    const select = (slug) => {
        const p = pages.find((x) => x.slug === slug);
        if (!p) return;
        setActive(slug);
        setForm({ title: p.title, bodyMarkdown: p.bodyMarkdown });
    };

    const save = async (e) => {
        e.preventDefault();
        if (!active) return;
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/policy-pages/${active}`, form);
            setPages((prev) => prev.map((p) => (p.slug === active ? data : p)));
            toast.success("Policy saved");
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };

    const activePage = pages.find((p) => p.slug === active);

    return (
        <div data-testid="admin-policy-pages-page">
            <span className="label-eyebrow">Legal</span>
            <h1 className="mt-2 font-display text-4xl text-inkDark">Policy pages</h1>
            <p className="mt-1 text-inkMuted">Edit shipping, returns, privacy and terms. Markdown supported.</p>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                <aside className="lg:col-span-3" data-testid="admin-policy-list">
                    {pages.map((p) => (
                        <button
                            key={p.slug}
                            type="button"
                            onClick={() => select(p.slug)}
                            className={`w-full text-left rounded-xl px-4 py-3 mb-2 border transition-colors ${p.slug === active ? "bg-skyblue/40 border-skyblue text-inkDark" : "bg-ivory border-inkDark/10 hover:border-skyblueDeep"}`}
                            data-testid={`admin-policy-tab-${p.slug}`}
                        >
                            <div className="font-medium text-inkDark">{p.title}</div>
                            <div className="text-xs text-inkMuted font-mono">/{p.slug}</div>
                        </button>
                    ))}
                </aside>
                <div className="lg:col-span-9">
                    {activePage ? (
                        <form onSubmit={save} className="space-y-4" data-testid="admin-policy-form">
                            <label className="block">
                                <span className="label-eyebrow">Title</span>
                                <input
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                                    data-testid="admin-policy-title-input"
                                />
                            </label>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <label className="block">
                                    <span className="label-eyebrow">Body (markdown)</span>
                                    <textarea
                                        rows={16}
                                        value={form.bodyMarkdown}
                                        onChange={(e) => setForm({ ...form, bodyMarkdown: e.target.value })}
                                        className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5 font-mono text-xs"
                                        data-testid="admin-policy-body-input"
                                    />
                                </label>
                                <div>
                                    <span className="label-eyebrow">Preview</span>
                                    <div className="mt-2 rounded-xl border border-inkDark/10 bg-ivory p-4 prose-policy text-sm text-inkDark/85 max-h-[420px] overflow-y-auto" data-testid="admin-policy-preview">
                                        <ReactMarkdown>{form.bodyMarkdown || ""}</ReactMarkdown>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="admin-policy-save-button">
                                    {busy ? "Saving…" : "Save policy"}
                                </button>
                            </div>
                        </form>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
